import { BaseButton, Button, CompoundButton, IPivot, IPivotItemProps, mergeStyleSets, Pivot, PivotItem, Stack } from '@fluentui/react';
import { withAITracking } from '@microsoft/applicationinsights-react-js';
import FileSaver from 'file-saver';
import { Attributes, CSSProperties, FC, MouseEventHandler, ReactNode, RefObject, useCallback, useMemo, useReducer, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsFeatureEnabled } from '../../api/features/features';
import {
    useExportReportCellAuditToExcel,
    useExportReportRowTraceToExcel,
    useGetReportCellAudit,
    useSaveAmounts,
} from '../../api/report/report';
import { applicationInsightsReactPlugin } from '../../ApplicationInsightsService';
import { IDrillDownValues } from '../../contexts/drillDownContext';
import { IReportDrillDownStyles } from '../../data-types/IReportDrillDownStyles';
import { IUnitTestProps } from '../../data-types/IUnitTestProps';
import { SelectedWhitePaperCell } from '../../data-types/selectedWhitePaperCell';
import { useDrillDown } from '../../hooks/useDrillDown';
import { useDrillDownStyles } from '../../hooks/useDrillDownStyles';
import {
    getNumberOfAccountGroupsWithAdjustments,
    getNumberOfUniqueAccounts,
    IReportDrillDownLoadedTrackingInformation,
    IReportDrilldownSavedTrackingInformation,
    usePerformanceMetricsForDrillDown,
} from '../../hooks/usePerformanceMetricsForDrillDown';
import {
    ExtractionDetailsAccountSummary,
    ExtractionDetailsAdjustmentInfo,
    ExtractionDetailsRequest,
    IWhitePaperColumn,
    IWhitePaperMappedRow,
    IWhitePaperMappedRowDictionary,
    ProblemDetails,
    Report,
    ReportCellAuditExportRequest,
    ReportCellAuditRequest,
    ReportRowTraceRequest,
    SaveAmountsRequest,
    SaveAmountsResponse,
    WhitePaperReportChanges,
} from '../../model';
import {
    AdjustmentAccountAmountAction,
    adjustmentAccountAmountDialogStateReducer,
    AdjustmentAccountAmountReducer,
    IAdjustmentAccountAmountDialogState,
} from '../../reducers/adjustmentAccountAmountReducer';
import {
    DrillDownPivotActionType,
    DrillDownPivotState,
    DrillDownStateReducer,
    drillDownStateReducer,
    ExpandCollapseAllButtonState,
    ExportButtonState,
    IErrorMessage,
} from '../../reducers/drillDownStateReducer';
import {
    EnterAmountsDialogReducer,
    enterAmountsDialogStateReducer,
    EnterAmountsModalAction,
    IEnterAmountsDialogState,
} from '../../reducers/enterAmountsDialogReducer';
import { getChangeTrackingData } from '../../utils/ChangeTrackingDataUtils';
import { mergeStylesOnDemand } from '../../utils/StyleUtils';
import { ErrorType } from '../../utils/UseCustomInstance';
import { ICardGridList } from '../card-grid-list/CardGridListInterfaces';
import { CardGridListExpandCollapseState, OnExpandCollapseStateChangeCallback } from '../card-grid-list/CardGridListTypes';
import { NarrowIconButton } from '../common/NarrowIconButton';
import { ToggleWidthPanel } from '../common/ToggleWidthPanel';
import ConfirmationDialog from '../dialog/ConfirmationDialog';
import EnterAmountsDialog from '../enter-amounts-dialog/EnterAmountsDialog';
import AccountAmountSection from './AccountAmountSection';
import AccountAmountTabPanel from './AccountAmountTabPanel';
import AdjustmentAccountAndAmountAssociationDialog from './AdjustmentAccountAndAmountAssociationDialog';
import ReportColumnContext from './ReportColumnContext';
import {
    buildExtractionRequest,
    consolidateAmountChangesForSave,
    getDefiningRowsFromDataSource,
    getUniqueExtractionDetailRequests,
    IAccountAmountWhitePaperRow,
    ITrackedExtractionDetailsAccountSummary,
    IWhitePaperRowTrace,
} from './ReportDrillDownUtils';
import TraceCalculationTabPanel from './TraceCalculationTabPanel';

export interface IReportDrillDownProps extends IUnitTestProps {
    onClose: (changeTrackingData: { amountsChangedOnServer: boolean; reportChanges: WhitePaperReportChanges }) => void;
    showPanel: boolean;
    report: Report;
    whitePaperReportMap: IWhitePaperMappedRowDictionary;
    whitePaperReportColumn: IWhitePaperColumn | undefined;
    changeTrackingData?: WhitePaperReportChanges;
    isMultiColumnWhitePaperReport?: boolean;
}

declare type ExpandCollapseAllOperation = 'Expand' | 'Collapse';

enum PostSaveChangesBehavior {
    SaveOnly = 0,
    SaveAndClose = 1,
}

export enum ElementPosition {
    Beginning,
    End,
}

function assignAccountSummaryEphemeralIds(summaries: ExtractionDetailsAccountSummary[]): ITrackedExtractionDetailsAccountSummary[] {
    let ephemeralKey: number = 0;

    for (const summary of Object.values<any>(summaries)) {
        summary.ephemeralKey = ++ephemeralKey;
    }

    return summaries as ITrackedExtractionDetailsAccountSummary[];
}

function addCustomPivotTabControls(instance: IPivot, styles: IReportDrillDownStyles) {
    if (instance == null) {
        return;
    }

    let customizedTabListContainer: HTMLElement | null = document.getElementById(customizedTabListId);

    if (customizedTabListContainer) {
        return;
    }

    const collapseButton: HTMLElement | null = document.getElementById(drilldownGridCollapseButtonId);
    const exportButton: HTMLElement | null = document.getElementById(drilldownExportButtonId);
    const pivotRoot: HTMLElement | null = document.getElementById(reportDrillDownPivotId);
    const tabList: HTMLElement | null = (pivotRoot?.firstElementChild as HTMLElement) ?? null;

    if (!(collapseButton && exportButton && pivotRoot && tabList)) {
        return;
    }
    customizedTabListContainer = document.createElement('div');
    customizedTabListContainer.id = customizedTabListId;
    customizedTabListContainer.className = mergeStylesOnDemand(styles.customizedTabList)();
    moveElementToNewParent(tabList, customizedTabListContainer);
    moveElementToNewParent(collapseButton, customizedTabListContainer);
    moveElementToNewParent(exportButton, customizedTabListContainer);
    moveElementToNewParent(customizedTabListContainer, pivotRoot, ElementPosition.Beginning);
    tabList.style.flexGrow = '1';
}

function moveElementToNewParent(element: HTMLElement, newParent: HTMLElement, elementPosition: ElementPosition = ElementPosition.End) {
    element.style.display = '';
    if (element.parentElement) {
        element.parentElement.removeChild(element);
    }

    switch (elementPosition) {
        case ElementPosition.End:
            newParent.appendChild(element);
            break;
        case ElementPosition.Beginning:
            const firstChild = newParent.firstElementChild;
            if (firstChild) {
                newParent.insertBefore(element, firstChild);
            }
            break;
    }
}

function handleExpandCollapseAllButtonClick(
    drillDownCtxData: IDrillDownValues,
    getActiveGridRefObject: (selectedIndex: number) => ICardGridList | null,
    operation: ExpandCollapseAllOperation
) {
    const activeGrid: ICardGridList | null = getActiveGridRefObject(drillDownCtxData.selectedTabKey);

    if (operation === 'Expand') {
        activeGrid?.expandAll();
    } else {
        activeGrid?.collapseAll();
    }
}

function handleExportButtonClick(selectedPivotIndex: number, exportFunctionMap: Map<number, () => void>) {
    if (!exportFunctionMap) {
        return;
    }
    const exportFunction: (() => void) | undefined = exportFunctionMap.get(selectedPivotIndex);

    if (exportFunction) {
        exportFunction();
    }
}

const customizedTabListId: string = 'customizedTabList';
const drilldownGridCollapseButtonId: string = 'drilldownGridCollapseButton';
const drilldownExportButtonId: string = 'drilldownExportButton';
const reportDrillDownPivotId: string = 'reportDrillDownPivot';

const ReportDrillDown: FC<IReportDrillDownProps> = (props: IReportDrillDownProps) => {
    const { trackDrilldownLoading, trackDrilldownLoaded, trackDrilldownSaving, trackDrilldownSaved } = usePerformanceMetricsForDrillDown();
    const { t } = useTranslation();
    const { styles } = useDrillDownStyles();
    const { data: isEnterAmountsEnabled } = useIsFeatureEnabled({ featureFlagName: 'EnterAmountsModalEnabled' });

    const expandText: string = t('expand');
    const collapseText: string = t('collapse');
    const accountAmountTabText: string = t('accountAmount').toString();
    const traceCalculationTabText: string = t('traceCalculation').toString();
    const getExpandCollapseAllTextForOperation = useCallback(
        (operation: ExpandCollapseAllOperation) => (operation === 'Expand' ? expandText : collapseText),
        [expandText, collapseText]
    );
    const headerText: string = t('reportDrillDown').toString();
    const drillDownCtxData: IDrillDownValues = useDrillDown();
    const whitePaperCellDataRowIndex = 1;
    const whitePaperReportMap: IWhitePaperMappedRowDictionary = props.whitePaperReportMap;

    interface IReportDrillDownPivotItem extends Attributes {
        componentRef: RefObject<ICardGridList>;
        value: IPivotItemProps;
    }

    const initialDrillDownPivotState: DrillDownPivotState = {
        activeTabIndex: drillDownCtxData.selectedTabKey,
        exportButtonStateMap: new Map<number, ExportButtonState>([
            [0, { onClick: exportAccountAmount, isProcessing: false } as ExportButtonState],
            [1, { onClick: exportTraceCalculationTab, isProcessing: false } as ExportButtonState],
        ]),
        expandCollapseAllButtonState: new Map<number, ExpandCollapseAllButtonState>([
            [0, { disabled: true, expandCollapseState: CardGridListExpandCollapseState.AllCollapsed } as ExpandCollapseAllButtonState],
            [1, { disabled: true, expandCollapseState: CardGridListExpandCollapseState.AllCollapsed } as ExpandCollapseAllButtonState],
        ]),
        showConfirmationDialog: false,
        currentAccountAmountSummaries: [] as ITrackedExtractionDetailsAccountSummary[],
        isInitializing: true,
        errorMessages: [] as IErrorMessage[],
        isServerError: false,
        initialAccountAmountSummaries: null,
        isFetchingOrSavingData: false,
        amountsChangedOnServer: false,
        accountAmountGridHasData: false,
        changeTrackingData: props.changeTrackingData!,
    } as DrillDownPivotState;
    const [drillDownPivotState, updateDrillDownPivotState] = useReducer<DrillDownStateReducer>(
        drillDownStateReducer,
        initialDrillDownPivotState
    );

    // migrated from AccountAmountTabPanel
    const showConfirmationDialog: boolean = drillDownPivotState.showConfirmationDialog;
    const currentAccountAmountSummaries = drillDownPivotState.currentAccountAmountSummaries;
    const isFetchingOrSavingData = drillDownPivotState.isFetchingOrSavingData;
    const errorMessages = drillDownPivotState.errorMessages;
    const isServerError = drillDownPivotState.isServerError;
    const initialAccountAmountSummaries = drillDownPivotState.initialAccountAmountSummaries;
    const { mutate: saveAmounts } = useSaveAmounts();
    const { mutate: getReportCellAudit } = useGetReportCellAudit();

    const isSaveButtonDisabled = currentAccountAmountSummaries?.length < 1 || isFetchingOrSavingData;
    const isDataLoaded = !!initialAccountAmountSummaries;
    const selectedCell: SelectedWhitePaperCell = drillDownCtxData.traceCalculationData![whitePaperCellDataRowIndex];
    const refAccountAmountTab: RefObject<ICardGridList> = useRef<ICardGridList>(null);
    const refTraceCalculationTab: RefObject<ICardGridList> = useRef<ICardGridList>(null);
    const expandButtonState: CardGridListExpandCollapseState = drillDownPivotState.expandCollapseAllButtonState.get(
        drillDownPivotState.activeTabIndex
    )!.expandCollapseState;
    const accountAmountGridHasData: boolean = initialAccountAmountSummaries === null ? false : initialAccountAmountSummaries.length > 0;
    const expandCollapseAllButtonText: string = useMemo(() => {
        if (drillDownPivotState.activeTabIndex === 0) {
            if (expandButtonState === CardGridListExpandCollapseState.AllCollapsed) {
                return expandText;
            }
            return collapseText;
        }
        return collapseText;
    }, [expandText, collapseText, drillDownPivotState.activeTabIndex, expandButtonState]);
    const expandCollapseAllButtonDisabled: boolean = useMemo(
        () =>
            drillDownPivotState.activeTabIndex === 0
                ? !accountAmountGridHasData
                : expandButtonState === CardGridListExpandCollapseState.AllCollapsed,
        [drillDownPivotState.activeTabIndex, accountAmountGridHasData, expandButtonState]
    );

    const onAccountAmountExpandCollapseStateChange: OnExpandCollapseStateChangeCallback = useCallback(
        (updatedExpandCollapseState: CardGridListExpandCollapseState) =>
            updateDrillDownPivotState({
                type: DrillDownPivotActionType.CardGridListExpandCollapseStateChange,
                eventTargetTabIndex: 0,
                updatedExpandCollapseState,
            }),
        [updateDrillDownPivotState]
    );
    const onTraceCalculationExpandCollapseStateChange: OnExpandCollapseStateChangeCallback = useCallback(
        (updatedExpandCollapseState: CardGridListExpandCollapseState) =>
            updateDrillDownPivotState({
                type: DrillDownPivotActionType.CardGridListExpandCollapseStateChange,
                eventTargetTabIndex: 1,
                updatedExpandCollapseState,
            }),
        [updateDrillDownPivotState]
    );
    const amountsChangedOnServer: boolean = drillDownPivotState.amountsChangedOnServer;
    const reportChanges: WhitePaperReportChanges = drillDownPivotState.changeTrackingData;

    const onAmountCellValueChange = useCallback(
        (event: any, item: IAccountAmountWhitePaperRow): void => {
            updateDrillDownPivotState({ type: DrillDownPivotActionType.AmountEdited, event, item });
        },
        [updateDrillDownPivotState]
    );

    const initialState: IAdjustmentAccountAmountDialogState = {
        jurisdiction: '',
        period: '',
        entity: '',
        adjustmentCode: '',
        case: '',
        location: '',
        show: false,
        rowNumber: '',
        columnId: props.whitePaperReportColumn!.originalColumnId!,
        changeTrackingData: props.changeTrackingData!,
        taxReturnKey: props.report.id!,
        isLoading: false,
        saveButtonIsDisabled: true,
    };

    const initialEnterAmountsDialogState: IEnterAmountsDialogState = {
        jurisdiction: '',
        period: '',
        entity: '',
        adjustmentCode: '',
        case: '',
        location: '',
        show: false,
        rowNumber: '',
        columnId: props.whitePaperReportColumn!.originalColumnId!,
        changeTrackingData: props.changeTrackingData!,
        taxReturnKey: props.report.id!,
        isLoading: false,
        saveButtonIsDisabled: true,
        rowDescription: '',
        buttonTrigger: undefined,
    };

    const [adjustmentAccountAmountDialogState, updateAdjustmentAccountAmountDialogState] = useReducer<AdjustmentAccountAmountReducer>(
        adjustmentAccountAmountDialogStateReducer,
        initialState
    );

    const [enterAmountsDialogState, updateEnterAmountsDialogState] = useReducer<EnterAmountsDialogReducer>(
        enterAmountsDialogStateReducer,
        initialEnterAmountsDialogState
    );

    const renderAdjustmentAccountAssociationDialogButton = useCallback(
        (item?: IAccountAmountWhitePaperRow) => {
            function renderAdjustmentAccountAssociationDialogButtonIcon(): JSX.Element {
                return (
                    <svg width='8' height='8' viewBox='0 0 8 8' fill='none' xmlns='http://www.w3.org/2000/svg'>
                        <path
                            d='M7.62684 0.373773C7.12849 -0.124575 6.32051 -0.124593 5.82214 0.373732L0.892193 5.30323C0.771502 5.42391 0.680569 5.57104 0.626596 5.73295L0.0171146 7.5614C-0.0228115 7.68118 0.0083627 7.81323 0.0976401 7.90251C0.186918 7.99179 0.318973 8.02296 0.438752 7.98303L2.26725 7.37354C2.42912 7.31958 2.57621 7.22868 2.69687 7.10803L7.6268 2.17855C8.1252 1.6802 8.12522 0.872154 7.62684 0.373773ZM6.29352 0.845158C6.53154 0.607162 6.91743 0.607171 7.15543 0.845178C7.39346 1.0832 7.39345 1.46911 7.15541 1.70713L6.66625 2.19625L5.80432 1.33432L6.29352 0.845158ZM5.33289 1.8057L6.19482 2.66763L2.22549 6.6366C2.17801 6.68408 2.12013 6.71985 2.05643 6.74108L0.860389 7.13976L1.25905 5.94377C1.28029 5.88005 1.31608 5.82215 1.36358 5.77466L5.33289 1.8057Z'
                            fill='#075A92'
                        />
                    </svg>
                );
            }

            // Only render if row is an adjustment entry or if row is an adjustment summary without child adjustment entry rows
            if (
                !(
                    item &&
                    (item.dataObjectType === 'AdjustmentEntry' ||
                        (item.dataObjectType === 'AdjustmentSummary' && item.adjustmentEntries?.length === 0))
                )
            ) {
                return null;
            }

            const onButtonClick: MouseEventHandler<
                HTMLAnchorElement | HTMLButtonElement | HTMLDivElement | HTMLSpanElement | BaseButton | Button
            > = (
                event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement | HTMLDivElement | HTMLSpanElement | BaseButton | Button>
            ) => {
                updateAdjustmentAccountAmountDialogState({
                    type: AdjustmentAccountAmountAction.ModalOpen,
                    row: item,
                    displayRowNumber: selectedCell.displayRowNumber!,
                });
                const rowDescriptionString: string = `Row ${selectedCell.description}, ${props.whitePaperReportColumn?.entityCode} ${props.whitePaperReportColumn?.entityName} `;
                updateEnterAmountsDialogState({
                    type: EnterAmountsModalAction.ModalOpen,
                    row: item,
                    displayRowNumber: selectedCell.displayRowNumber!,
                    rowDescription: rowDescriptionString,
                    buttonTrigger: event.target as HTMLElement,
                });
            };
            return (
                <CompoundButton
                    onClick={onButtonClick}
                    onRenderIcon={renderAdjustmentAccountAssociationDialogButtonIcon}
                    styles={styles.adjustmentAccountAssociationDialogButton}
                    tabIndex={0}
                ></CompoundButton>
            );
        },
        [updateAdjustmentAccountAmountDialogState, updateEnterAmountsDialogState, styles.adjustmentAccountAssociationDialogButton]
    );

    const accountAmountTabPanel = useMemo(() => {
        return (
            <AccountAmountTabPanel
                isUnitTestExecution={props.isUnitTestExecution}
                cardGridListRef={refAccountAmountTab}
                onExpandCollapseStateChange={onAccountAmountExpandCollapseStateChange}
                key={`AccountAmountTabPanel ${drillDownCtxData.traceCalculationData[whitePaperCellDataRowIndex].displayRowNumber}`}
                currentAccountAmountSummaries={currentAccountAmountSummaries}
                isFetchingOrSavingData={isFetchingOrSavingData}
                isDataLoaded={isDataLoaded}
                initialAccountAmountSummaries={initialAccountAmountSummaries}
                selectedCell={selectedCell}
                onAmountCellValueChange={onAmountCellValueChange}
                updateDrillDownState={updateDrillDownPivotState}
                changeTrackingData={props.changeTrackingData}
                whitePaperReportColumn={props.whitePaperReportColumn!}
                taxReturnKey={props.report.id!}
                renderAdjustmentAccountAssociationDialogButton={renderAdjustmentAccountAssociationDialogButton}
            />
        );
    }, [
        props.onClose,
        amountsChangedOnServer,
        props.isUnitTestExecution,
        currentAccountAmountSummaries,
        isFetchingOrSavingData,
        isDataLoaded,
        initialAccountAmountSummaries,
        selectedCell,
        onAmountCellValueChange,
        onAccountAmountExpandCollapseStateChange,
        updateDrillDownPivotState,
        props.changeTrackingData,
    ]);

    const traceCalculationTabPanel = useMemo(() => {
        return (
            <TraceCalculationTabPanel
                data={drillDownCtxData.traceCalculationData}
                isUnitTestExecution={props.isUnitTestExecution}
                whitePaperCellDataRowIndex={whitePaperCellDataRowIndex}
                whitePaperReportMap={whitePaperReportMap}
                cardGridListRef={refTraceCalculationTab}
                onExpandCollapseStateChange={onTraceCalculationExpandCollapseStateChange}
                key={`TraceCalculationTabPanel ${drillDownCtxData.traceCalculationData[whitePaperCellDataRowIndex].displayRowNumber}`}
                report={props.report}
                whitePaperReportColumn={props.whitePaperReportColumn}
                isFetchingOrSavingData={isFetchingOrSavingData}
            />
        );
    }, [drillDownCtxData.traceCalculationData, whitePaperReportMap, selectedCell, onTraceCalculationExpandCollapseStateChange]);

    const pivotItemContent: IReportDrillDownPivotItem[] = useMemo(
        () => [
            {
                componentRef: refAccountAmountTab,
                value: {
                    itemKey: '0',
                    headerText: accountAmountTabText,
                    alwaysRender: true,
                    children: [accountAmountTabPanel] as ReactNode[],
                } as IPivotItemProps,
                key: 'accountAmountPivotItem',
            } as IReportDrillDownPivotItem,
            {
                componentRef: refTraceCalculationTab,
                value: {
                    itemKey: '1',
                    headerText: traceCalculationTabText,
                    alwaysRender: true,
                    children: [traceCalculationTabPanel] as ReactNode[],
                } as IPivotItemProps,
                key: 'traceCalculationPivotItem',
            } as IReportDrillDownPivotItem,
        ],
        [
            accountAmountTabText,
            accountAmountTabPanel,
            refAccountAmountTab,
            refTraceCalculationTab,
            traceCalculationTabPanel,
            traceCalculationTabText,
        ]
    );

    const pivotItemStyle: CSSProperties = useMemo(() => {
        return { height: '100%' };
    }, []);
    const pivotItems: JSX.Element[] = useMemo(
        () =>
            pivotItemContent.map((item: IReportDrillDownPivotItem) => <PivotItem {...item.value} key={item.key} style={pivotItemStyle} />),
        [pivotItemContent, pivotItemStyle]
    );

    function getActiveGridRefObject(selectedIndex: number) {
        return pivotItemContent[selectedIndex]?.componentRef?.current ?? null;
    }

    const onPivotLinkClicked = useCallback(
        (item: PivotItem | undefined) => {
            if (!item?.props?.itemKey) {
                return;
            }
            const pivotItemIndex = Number(item?.props?.itemKey);
            if (pivotItemIndex < 0) {
                return;
            }

            drillDownCtxData.selectedTabKey = pivotItemIndex;
            updateDrillDownPivotState({
                type: DrillDownPivotActionType.ChangeActiveTabIndex,
                newActiveTab: pivotItemIndex,
                eventTargetTabIndex: pivotItemIndex,
            });
        },
        [getExpandCollapseAllTextForOperation, accountAmountGridHasData, drillDownCtxData]
    );
    const onSaveButtonClick: (postSaveChangesBehavior: PostSaveChangesBehavior) => void = useCallback(
        (postSaveChangesBehavior: PostSaveChangesBehavior) => {
            updateDrillDownPivotState({ type: DrillDownPivotActionType.StartProcessingSaveAmounts });
            trackDrilldownSaving();
            try {
                const realGroupsToSave: SaveAmountsRequest = {
                    accountSummaryUpdates: consolidateAmountChangesForSave(currentAccountAmountSummaries),
                    taxReturnKey: props.report.id!,
                    changesTrackingData: getChangeTrackingData(
                        selectedCell.displayRowNumber!,
                        props.whitePaperReportColumn!.originalColumnId!,
                        props.changeTrackingData!,
                        currentAccountAmountSummaries
                    ),
                };
                //set flag here
                saveAmounts(
                    {
                        data: realGroupsToSave,
                    },
                    {
                        onSettled: (
                            data: SaveAmountsResponse | undefined,
                            error: ErrorType<ProblemDetails> | null,
                            variables: { data: SaveAmountsRequest },
                            context: unknown | undefined
                        ) => {
                            const numberOfAccountsSaved: number = variables.data.accountSummaryUpdates!.length;
                            const numberOfAmountsSaved: number = variables.data
                                .accountSummaryUpdates!.filter((x: ExtractionDetailsAccountSummary) => x.adjustmentInfo)
                                .flatMap((x: ExtractionDetailsAccountSummary) => x.adjustmentInfo!)
                                .flatMap((x: ExtractionDetailsAdjustmentInfo) => x.entries).length;
                            const drillDownTrackingSaved: IReportDrilldownSavedTrackingInformation = {
                                numberOfAmountsSaved: numberOfAmountsSaved,
                                reportId: props.report?.id?.toString() ?? 'N/A',
                                isMultiColumnReport: props.isMultiColumnWhitePaperReport || false,
                                reportIsForEntityGroup: props.report.isForEntityGroup!,
                                success: error ? false : true,
                                columnId: props.whitePaperReportColumn?.originalColumnId?.toString() ?? 'N/A',
                                numberOfAccountGroups: numberOfAccountsSaved,
                                rowNumber: selectedCell.displayRowNumber!,
                            };
                            trackDrilldownSaved(drillDownTrackingSaved);

                            if (!error && postSaveChangesBehavior === PostSaveChangesBehavior.SaveAndClose && props.onClose) {
                                // we can't use the state variable directly because it won't be updated until the next re-render,
                                // at which point this component will be closed anyway
                                props.onClose({ amountsChangedOnServer: true, reportChanges: variables.data.changesTrackingData! });
                            } else {
                                updateDrillDownPivotState({ type: DrillDownPivotActionType.SaveAmountsComplete });
                            }
                        },
                        onSuccess: (
                            data: SaveAmountsResponse | undefined,
                            variables: { data: SaveAmountsRequest },
                            context: unknown | undefined
                        ) => {
                            updateDrillDownPivotState({
                                type: DrillDownPivotActionType.SaveAmountsSuccess,
                                updatedChangeTrackingData: variables.data.changesTrackingData!,
                            });
                        },
                        onError: (
                            error: ErrorType<ProblemDetails> | null,
                            variables: { data: SaveAmountsRequest },
                            context: unknown | undefined
                        ) => {
                            updateDrillDownPivotState({ type: DrillDownPivotActionType.SaveAmountsFailure, error });
                        },
                    }
                );
            } catch (error: any) {
                if (error) {
                    throw new Error(error?.request?.response);
                }
            }
        },
        [updateDrillDownPivotState, props.onClose, currentAccountAmountSummaries, saveAmounts]
    );
    const showConfirmationDialogOnExit: boolean = drillDownPivotState.showConfirmationDialogOnExit;
    const onDrillDownClose: () => void = useCallback(() => {
        if (showConfirmationDialogOnExit) {
            updateDrillDownPivotState({ type: DrillDownPivotActionType.ConfirmationDialogOpened });
        } else if (props.onClose) {
            props.onClose({ amountsChangedOnServer, reportChanges });
        }
    }, [
        currentAccountAmountSummaries,
        props.onClose,
        amountsChangedOnServer,
        reportChanges,
        updateDrillDownPivotState,
        updateEnterAmountsDialogState,
        showConfirmationDialogOnExit,
        enterAmountsDialogState.show,
    ]);

    const rowDefinition: IWhitePaperMappedRow = props.whitePaperReportMap[selectedCell.displayRowNumber!];
    const traces: IWhitePaperRowTrace[] = getDefiningRowsFromDataSource(
        selectedCell,
        props.whitePaperReportColumn,
        props.whitePaperReportMap
    );

    const requestCellAudit = () => {
        const requests: ExtractionDetailsRequest[] = buildExtractionRequest(
            traces,
            rowDefinition,
            props.whitePaperReportColumn,
            props.report.id!
        );
        updateDrillDownPivotState({ type: DrillDownPivotActionType.RequestCellAuditStart, extractionDetailsRequests: requests });

        if (requests.length === 0) {
            const drillDownTrackingNoAttemptMade: IReportDrillDownLoadedTrackingInformation = {
                reportId: props.report?.id?.toString() ?? 'N/A',
                numberOfAccountGroups: 0,
                numberOfAccountGroupsWithAdjustments: 0,
                rowMapItemCount: Object.keys(props.whitePaperReportMap).length,
                success: 'Not Attempted',
                rowRequestCount: 0,
                isMultiColumnReport: props.isMultiColumnWhitePaperReport || false,
                reportIsForEntityGroup: false,
                numberOfUniqueAccounts: 0,
                columnId: props.whitePaperReportColumn?.originalColumnId?.toString() ?? 'N/A',
                rowNumber: selectedCell.displayRowNumber!,
            };
            trackDrilldownLoaded(drillDownTrackingNoAttemptMade);
            updateDrillDownPivotState({ type: DrillDownPivotActionType.RequestCellAuditEnd });
            return;
        }

        const extractionDetailRequest: ExtractionDetailsRequest[] = getUniqueExtractionDetailRequests(requests);
        const reportCellAuditRequest: ReportCellAuditRequest = {
            context: {
                reportCase: props.report.case,
                columnEntityCode: props.whitePaperReportColumn?.entityCode,
                reportJurisdiction: props.report.jurisdiction,
                reportTaxYear: props.report.year,
                reportLocation: props.report.location,
            },
            extractionDetailsRequests: extractionDetailRequest,
        };

        try {
            getReportCellAudit(
                {
                    data: reportCellAuditRequest,
                },
                {
                    onSettled: (
                        data: ExtractionDetailsAccountSummary[] | undefined,
                        error: ErrorType<ProblemDetails> | null,
                        variables: { data: ReportCellAuditRequest },
                        context: unknown | undefined
                    ) => {
                        const drilldownLoadedTracking: IReportDrillDownLoadedTrackingInformation = {
                            reportId: props.report?.id?.toString() ?? 'N/A',
                            numberOfAccountGroups: data?.length ?? 0,
                            numberOfAccountGroupsWithAdjustments: getNumberOfAccountGroupsWithAdjustments(data),
                            rowMapItemCount: Object.keys(props.whitePaperReportMap).length,
                            isMultiColumnReport: props.isMultiColumnWhitePaperReport || false,
                            success: error ? false : true,
                            rowRequestCount: extractionDetailRequest.length,
                            reportIsForEntityGroup: props.report!.isForEntityGroup!,
                            numberOfUniqueAccounts: getNumberOfUniqueAccounts(data),
                            columnId: props.whitePaperReportColumn?.originalColumnId?.toString() ?? 'N/A',
                            rowNumber: selectedCell.displayRowNumber!,
                        };
                        trackDrilldownLoaded(drilldownLoadedTracking);
                        updateDrillDownPivotState({ type: DrillDownPivotActionType.RequestCellAuditEnd });
                    },
                    onSuccess: (data: ExtractionDetailsAccountSummary[]) => {
                        updateDrillDownPivotState({
                            type: DrillDownPivotActionType.RequestCellAuditSuccess,
                            initialAccountAmountSummaries: assignAccountSummaryEphemeralIds(data),
                        });
                    },
                    onError: (error: ErrorType<ProblemDetails>) => {
                        updateDrillDownPivotState({ type: DrillDownPivotActionType.RequestCellAuditFailure, error });
                    },
                }
            );
        } catch (error: any) {
            if (error) {
                throw new Error(error?.request?.response);
            }
        }
    };

    const renderErrorMessages = () => {
        return (
            <ul>
                {errorMessages?.map((message) => {
                    const messageContent: string = message.needsTranslation
                        ? t(message.message, message.translationArgs).toString()
                        : message.message;
                    return <li key={messageContent}>{messageContent}</li>;
                })}
            </ul>
        );
    };

    // make sure we only do this once
    if (drillDownPivotState.isInitializing) {
        trackDrilldownLoading();
        requestCellAudit();
    }

    function onConfirmationModalSubmit(event: React.MouseEvent<HTMLElement, MouseEvent>): void {
        onSaveButtonClick(PostSaveChangesBehavior.SaveAndClose);
    }

    const exportTraceCalcHook = useExportReportRowTraceToExcel();
    const exportAccountAmountHook = useExportReportCellAuditToExcel();
    function exportTraceCalculationTab() {
        updateDrillDownPivotState({ type: DrillDownPivotActionType.StartProcessingExport, eventTargetTabIndex: 1 });
        const reportRowTraceRequest: ReportRowTraceRequest = {
            columnFieldName: props.whitePaperReportColumn?.fieldName,
            rowNumber: selectedCell.displayRowNumber,
            reportId: props.report.id,
            reportName: props.report.name,
            period: props.report.period,
            executedDate: props.report.executed,
        } as ReportRowTraceRequest;

        exportTraceCalcHook.mutate(
            { data: reportRowTraceRequest },
            {
                onSuccess: (data: Blob) => {
                    FileSaver.saveAs(
                        data,
                        `TraceCalculation - ${props.report.name} - Row ${rowDefinition.rowNumber} - ${new Date().toISOString()}.xlsx`
                    );
                },
                onError: (error: ErrorType<ProblemDetails>) => {
                    updateDrillDownPivotState({ type: DrillDownPivotActionType.ExportFailure, eventTargetTabIndex: 1 });
                },
                onSettled: (
                    data: Blob | undefined,
                    error: ErrorType<ProblemDetails> | null,
                    variables: { data: ReportRowTraceRequest },
                    context: unknown | undefined
                ) => {
                    updateDrillDownPivotState({ type: DrillDownPivotActionType.StopProcessingExport, eventTargetTabIndex: 1 });
                },
            }
        );
    }
    function exportAccountAmount() {
        updateDrillDownPivotState({ type: DrillDownPivotActionType.StartProcessingExport, eventTargetTabIndex: 0 });
        const reportCellAuditRequest = {
            extractionDetailsRequests: buildExtractionRequest(traces, rowDefinition, props.whitePaperReportColumn, props.report.id!),
            entityName: props.whitePaperReportColumn?.entityName,
            reportName: props.report.name,
            executedAt: props.report.executed,
            period: props.report.period,
        } as ReportCellAuditExportRequest;
        exportAccountAmountHook.mutate(
            { data: reportCellAuditRequest },
            {
                onSuccess: (data: Blob) => {
                    FileSaver.saveAs(
                        data,
                        `AccountAmountExport - ${props.report.name} - Row ${rowDefinition.rowNumber} - ${new Date().toISOString()}.xlsx`
                    );
                },
                onError: (error: ErrorType<ProblemDetails>) => {
                    updateDrillDownPivotState({ type: DrillDownPivotActionType.ExportFailure, eventTargetTabIndex: 0 });
                },
                onSettled: (
                    data: Blob | undefined,
                    error: ErrorType<ProblemDetails> | null,
                    variables: { data: ReportCellAuditRequest },
                    context: unknown | undefined
                ) => {
                    updateDrillDownPivotState({ type: DrillDownPivotActionType.StopProcessingExport, eventTargetTabIndex: 0 });
                },
            }
        );
    }

    const exportFunctionMap = useMemo(
        () =>
            new Map<number, () => void>([
                [0, exportAccountAmount],
                [1, exportTraceCalculationTab],
            ]),
        [exportAccountAmount, exportTraceCalculationTab]
    );

    const expandAllClickHandler = useCallback(() => {
        const operation = expandCollapseAllButtonText === expandText ? 'Expand' : 'Collapse';
        return handleExpandCollapseAllButtonClick(drillDownCtxData, getActiveGridRefObject, operation);
    }, [drillDownCtxData, getActiveGridRefObject, expandCollapseAllButtonText, handleExpandCollapseAllButtonClick, expandText]);
    const exportClickHandler = useCallback(
        () => handleExportButtonClick(drillDownPivotState.activeTabIndex, exportFunctionMap),
        [drillDownPivotState, exportFunctionMap]
    );
    const confirmationDialogCancelButtonHandler = useCallback(
        (event: React.MouseEvent<HTMLElement>) => props!.onClose({ amountsChangedOnServer, reportChanges: reportChanges }),
        [props.onClose, amountsChangedOnServer, reportChanges]
    );
    const confirmationDialogOnDismissHandler = useCallback(
        () => updateDrillDownPivotState({ type: DrillDownPivotActionType.ConfirmationDialogClosed }),
        [updateDrillDownPivotState]
    );
    const panelOnConfirmHandler = useCallback(() => onSaveButtonClick(PostSaveChangesBehavior.SaveOnly), [onSaveButtonClick]);
    const pivotWrapperClassName: string = useMemo(() => mergeStylesOnDemand(styles.pivotWrapper)(), [styles.pivotWrapper]);
    const pivotStyleSets = useMemo(() => mergeStyleSets(styles.pivot), [styles.pivot]);
    const updateChangeTrackingData = useCallback(
        (changeTrackingData: WhitePaperReportChanges) =>
            updateDrillDownPivotState({
                type: DrillDownPivotActionType.AdjustmentModalAmountsSave,
                updatedChangeTrackingData: changeTrackingData,
            }),
        [updateDrillDownPivotState]
    );
    return (
        <ToggleWidthPanel
            isOpen={props.showPanel}
            onClose={onDrillDownClose}
            onConfirm={panelOnConfirmHandler}
            primaryButtonDisabled={isSaveButtonDisabled}
            headerText={headerText}
        >
            <Stack id='drilldownHeader' className={mergeStylesOnDemand(styles.header)()}>
                <AccountAmountSection data={selectedCell} />
                <ReportColumnContext
                    case={props.report.case ?? ''}
                    entity={props.whitePaperReportColumn?.entityCode ?? '' ?? ''}
                    jurisdiction={props.report.jurisdiction ?? ''}
                    year={props.report.year?.toString() ?? ''}
                />
                {isServerError && (
                    <div className={mergeStylesOnDemand(styles.errorMessage)()}>
                        <p>{t('dataLoadError')}</p>
                        <p>{t('retryOrContactAdmin')}</p>
                        {renderErrorMessages()}
                    </div>
                )}
                {!isServerError && (errorMessages?.length ?? 0) > 0 && (
                    <div className={mergeStylesOnDemand(styles.errorMessage)()}>{renderErrorMessages()}</div>
                )}
            </Stack>
            <Pivot
                aria-label={headerText}
                componentRef={{
                    set current(instance: IPivot) {
                        addCustomPivotTabControls(instance, styles);
                    },
                }}
                defaultSelectedKey={props.isUnitTestExecution ? drillDownPivotState.activeTabIndex.toString() : undefined}
                id={reportDrillDownPivotId}
                onLinkClick={onPivotLinkClicked}
                selectedKey={!props.isUnitTestExecution ? drillDownPivotState.activeTabIndex.toString() : undefined}
                styles={pivotStyleSets}
                className={pivotWrapperClassName}
                key='reportDrillDownPivot'
            >
                {pivotItems}
            </Pivot>
            <NarrowIconButton
                aria-label={expandCollapseAllButtonText}
                disabled={expandCollapseAllButtonDisabled}
                id={drilldownGridCollapseButtonId}
                onClick={expandAllClickHandler}
                iconName={expandCollapseAllButtonText === expandText ? 'AddCircleRegular' : 'SubtractCircleRegular'}
                style={{ display: 'none' }}
                styles={styles.expandAllButton}
                label={expandCollapseAllButtonText}
            />
            <NarrowIconButton
                id={drilldownExportButtonId}
                onClick={exportClickHandler}
                iconName='ArrowDownloadRegular'
                style={{ display: 'none' }}
                styles={styles.exportButton}
                isProcessing={drillDownPivotState.exportButtonStateMap.get(drillDownPivotState.activeTabIndex)?.isProcessing}
                label={t('export').toString()}
                processingLabel={t('loading').toString()}
                disabled={drillDownPivotState.exportButtonStateMap.get(drillDownPivotState.activeTabIndex)?.isProcessing}
            />
            {showConfirmationDialogOnExit && (
                <ConfirmationDialog
                    show={showConfirmationDialog}
                    filterActionBarProps={{
                        primaryButtonLabel: 'saveChanges',
                        primaryHandler: onConfirmationModalSubmit,
                        secondaryHandler: confirmationDialogCancelButtonHandler,
                        secondaryButtonLabel: 'exitWithoutSaving',
                    }}
                    heading={t('saveChanges')}
                    content={t('preventNavigationDialogContent')}
                    onDismiss={confirmationDialogOnDismissHandler}
                />
            )}

            {isEnterAmountsEnabled
                ? enterAmountsDialogState.show && (
                      <EnterAmountsDialog
                          {...enterAmountsDialogState}
                          updateState={updateEnterAmountsDialogState}
                          updateChangeTrackingData={updateChangeTrackingData}
                      />
                  )
                : adjustmentAccountAmountDialogState.show && (
                      <AdjustmentAccountAndAmountAssociationDialog
                          {...adjustmentAccountAmountDialogState}
                          updateState={updateAdjustmentAccountAmountDialogState}
                          updateChangeTrackingData={updateChangeTrackingData}
                      />
                  )}
        </ToggleWidthPanel>
    );
};

export default withAITracking(applicationInsightsReactPlugin, ReportDrillDown, 'Report Drill Down');
